import {useCallback, useEffect, useRef, useState} from 'react';

import {useSelector} from 'react-redux';
import PlacesAutocomplete, {geocodeByAddress} from 'react-places-autocomplete';

import {isLocationAccessPermitted} from '~/shared/services/geocoder';
import {getLocalizationService} from '~/shared/services/localisationService';
import useGoogleMapScript from '~/shared/hooks/useGoogleMapScript';
import {createLogger} from '~/shared/logging';
import {selectCurrentAddressKey} from '~/shared/store/selectors';
import {trackEvent} from '~/shared/services/analytics';

import {
  DropdownContainer,
  Input,
  SuggestionItem,
  BoldResultContainer,
  PoweredByGoogleWrapper,
  PoweredByGoogleImg,
  SuggestionItemIcon,
  ScreenReaderNotificator,
  StyledSearchIconSvg,
} from './CustomAddressAutoComplete.styled';

const logger = createLogger('CustomAddressAutoComplete');

// Todo: refactor to be using hooks

const SEARCH_OPTIONS = {
  componentRestrictions: {country: ['ps', 'il']},
  types: ['geocode', 'establishment'],
};

const CURRENT_LOCATION_PLACE_ID = 'CURRENT_LOCATION';

const SuggestionResultWithBoldParts = ({matchedSubstrings, description}) => {
  if (!matchedSubstrings) {
    return description;
  }

  const boldParts = matchedSubstrings.reduce(
    (allParts, part) => allParts.concat(description.substr(part.offset, part.length)),
    [],
  );
  const regexStringContainBoldParts = new RegExp(`(${boldParts.join('|')})`, 'ig');

  return (
    <BoldResultContainer
      dangerouslySetInnerHTML={{__html: description.replace(regexStringContainBoldParts, '<b>$1</b>')}}
    />
  );
};

const CustomAddressAutoComplete = ({
  itemHeight,
  inputRef,
  placeholder,
  resetAddress,
  isMaxMobile,
  isAddAddressMode,
  onCurrectLocationClick,
  onPlaceSelected,
}) => {
  const {t} = getLocalizationService();
  const [address, setAddress] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [locationAccessEnabled, setLocationAccessEnabled] = useState(true);
  const currentAddressKey = useSelector(selectCurrentAddressKey);
  const placesAutocompleteRef = useRef(null);

  useEffect(() => {
    const setLocationAccess = async () => {
      const isLocationAccessible = await isLocationAccessPermitted();
      await setLocationAccessEnabled(isLocationAccessible);
    };

    setLocationAccess();
  }, []);

  const updatePlacesAutoComplete = useCallback(async () => {
    if (placesAutocompleteRef?.current && !placesAutocompleteRef?.current.addedCurrentLocationSuggestion) {
      // using both snake_case and camelCase for properties, because of how react-places-autocomplete.
      const currentLocationSuggestion = {
        id: 0,
        index: 0,
        active: false,
        place_id: CURRENT_LOCATION_PLACE_ID,
        placeId: CURRENT_LOCATION_PLACE_ID,
        description: locationAccessEnabled ? t('use_my_current_location') : t('location_access_denied'),
        structured_formatting: {
          main_text: ' ',
          secondary_text: ' ',
        },
        structuredFormatting: {
          mainText: ' ',
          secondaryText: ' ',
        },
      };

      // overriding clearSuggestions functions to prevent suggestions clear.
      const {clearSuggestions} = placesAutocompleteRef.current;
      placesAutocompleteRef.current.clearSuggestions = isTriggered => (isTriggered ? clearSuggestions() : {});
      // adding the current location suggestion into places auto complete
      // to make the keyboard events to work on initiating.
      placesAutocompleteRef.current.state.suggestions = [currentLocationSuggestion];

      const originalAutocompleteCallback = placesAutocompleteRef.current.autocompleteCallback;
      placesAutocompleteRef.current.autocompleteCallback = (predictions, status) => {
        const predictionsWithCurrentLocation = [currentLocationSuggestion, ...(predictions || [])];
        originalAutocompleteCallback(predictionsWithCurrentLocation, status);
      };

      placesAutocompleteRef.current.addedCurrentLocationSuggestion = true;
    }
  }, [locationAccessEnabled, t]);

  const onInputClick = () => {
    if (isMaxMobile) {
      setAddress('');
      resetAddress();
    }

    if (!isInputFocused) {
      setIsInputFocused(true);
    }
  };

  const onChange = newAddress => {
    if (newAddress.length === 2) {
      trackEvent('addressStartSearch', {
        searchOption: 'typing',
      });
    }
    resetAddress();
    setAddress(newAddress);
  };

  const handleSelect = async (selectedAddressString, placeId) => {
    resetAddress();

    if (placeId === CURRENT_LOCATION_PLACE_ID) {
      await onCurrectLocationClick({setAddress});
      setIsInputFocused(false);
      return;
    }

    setAddress(selectedAddressString);
    const results = await geocodeByAddress(selectedAddressString);
    if (!results) {
      return;
    }
    const result = {...results[0], name: results[0].formatted_address};
    onPlaceSelected(result);
    setIsInputFocused(false);
    inputRef.current?.blur();
  };

  const onPlacesAutocompleteRef = useCallback(
    ref => {
      placesAutocompleteRef.current = ref;
      if (ref) {
        updatePlacesAutoComplete();
      }
    },
    [placesAutocompleteRef, updatePlacesAutoComplete],
  );

  const isGoogleMapReady = useGoogleMapScript();

  if (!isGoogleMapReady) {
    return null;
  }

  const handleAutocompleteError = (status, clearSuggestions) => {
    if (status !== 'ZERO_RESULTS') {
      logger.error('search error', status);
    }
    clearSuggestions(true);
  };

  return (
    <PlacesAutocomplete
      value={address}
      onChange={onChange}
      onSelect={handleSelect}
      searchOptions={SEARCH_OPTIONS}
      ref={onPlacesAutocompleteRef}
      debounce={500}
      onError={handleAutocompleteError}
    >
      {({getInputProps, suggestions, getSuggestionItemProps}) => {
        const getInputPropsValues = getInputProps({
          ref: inputRef,
          onFocus: () => setIsInputFocused(true),
          onBlur: () => setIsInputFocused(false),
          onKeyDown: () => !isInputFocused && setIsInputFocused(true),
          onClick: onInputClick,
          itemHeight,
          placeholder,
        });

        return (
          <>
            <ScreenReaderNotificator>{t('type_address_for_delevery')}</ScreenReaderNotificator>
            <Input
              autoFocus={isAddAddressMode && !currentAddressKey}
              aria-controls="homePage_DropDownAddress"
              {...getInputPropsValues}
              isAddAddressMode={isAddAddressMode}
            />
            <StyledSearchIconSvg />
            {isInputFocused && (
              <DropdownContainer>
                {suggestions.map(suggestion => {
                  const isCurrentLocation = suggestion.placeId === CURRENT_LOCATION_PLACE_ID;
                  return (
                    <SuggestionItem
                      isCurrentLocation={isCurrentLocation}
                      active={suggestion.active}
                      {...{itemHeight, ...getSuggestionItemProps(suggestion)}}
                      id={suggestion.placeId}
                      key={suggestion.placeId}
                      isAddAddressMode={isAddAddressMode}
                    >
                      <SuggestionItemIcon isCurrentLocation={isCurrentLocation} />

                      <SuggestionResultWithBoldParts
                        matchedSubstrings={suggestion.matchedSubstrings}
                        description={suggestion.description}
                      />
                    </SuggestionItem>
                  );
                })}
                <PoweredByGoogleWrapper shouldShow={suggestions.length > 1}>
                  <PoweredByGoogleImg alt="Powered by google icon" />
                </PoweredByGoogleWrapper>
              </DropdownContainer>
            )}
          </>
        );
      }}
    </PlacesAutocomplete>
  );
};

export default CustomAddressAutoComplete;
